// NOTE: The window.dataLayer is a JavaScript object used in Google Tag Manager (GTM)
// to store and manage data. It is a virtual layer that sits between a website
// or application and the data processing tools, such as Google Analytics.

import * as Sentry from "@sentry/react";
import mixpanel from "mixpanel-browser";
import { env } from "../../config/env";
import { DebugLogger } from "../../domains/app/utils/debugLogger";
import { extractRoomIdFromPath } from "../../domains/room/utils/roomIdFromPath";
import { getCurrentUserData } from "../../domains/user/hooks/useCurrentUser";
import {
  mixpanelInitialized,
  whenMixpanelInitialized,
} from "../../hooks/use-mixpanel";
import { getApiQueryData } from "../../services/api";
import { AnalyticsItemList } from "./analytics.types";
import { newEventId } from "./eventIdentity";

type DataLayerEntry = Record<string, unknown>;

/**
 * Maps GA event names to Mixpanel event names matching the product analytics
 * spec. Events not listed here are GA-only and will NOT be sent to Mixpanel.
 *
 * Static entries:
 *   `event`  – Mixpanel event name, or `null` to skip Mixpanel entirely.
 *   `props`  – Default properties merged under the caller's (caller wins).
 *
 * Dynamic entries:
 *   `resolve(params)` – Returns `{ event, props? }` or `null` to skip,
 *                        used when a single GA event maps to different
 *                        Mixpanel events based on properties.
 */
type MixpanelStaticMapping = {
  event: string | null;
  props?: Record<string, unknown>;
};
type ResolveParams = Record<string, unknown> & {
  _contentLocation: "public app" | "room" | "profile";
  _contentOrigin: string;
};
type MixpanelDynamicMapping = {
  resolve: (params: ResolveParams) => {
    event: string;
    props?: Record<string, unknown>;
    /** When true, send only `props` to Mixpanel — do not merge the GA payload. */
    exclusive?: boolean;
  } | null;
};
type MixpanelMapping = MixpanelStaticMapping | MixpanelDynamicMapping;

// ── Mixpanel-only helpers ───────────────────────────────────────────────

/** Derive content location from the current URL path. */
export function getContentLocation(): "public app" | "room" | "profile" {
  const pathname = globalThis.location.pathname;
  if (pathname.startsWith("/room/")) return "room";
  if (pathname.startsWith("/profile")) return "profile";
  return "public app";
}

/** "public app" when outside a room, otherwise the current room's display name. */
export function getContentOrigin(): string | null {
  const roomId = extractRoomIdFromPath(globalThis.location.pathname);
  if (roomId === null) return "public app";
  const room = getApiQueryData("/webapp/private/rooms/:id", {
    param: { id: roomId },
  });
  return room === null ? null : room.translations.en.name;
}

/** Shared origin/content_origin props — use in every content-type event. */
function originProps(params: ResolveParams): {
  origin: string;
  content_origin: string;
} {
  return {
    origin: params._contentLocation,
    content_origin: params._contentOrigin,
  };
}

/** Extract the first ecommerce item from a GA4 payload. */
function firstEcommerceItem(
  params: Record<string, unknown>
): Record<string, unknown> | undefined {
  const items = (params.ecommerce as Record<string, unknown>)
    .items as unknown[];
  return items[0] as Record<string, unknown> | undefined;
}

/** Build shared props for content_liked / content_saved / content_shared. */
function contentInteractionProps(
  contentType: string,
  params: ResolveParams
): Record<string, unknown> {
  const listName = firstEcommerceItem(params)?.item_list_name as
    string | undefined;
  return {
    content_type: contentType,
    navigation_context: listName
      ? mapListNameToNavigationContext(listName)
      : undefined,
    ...originProps(params),
  };
}

/** Map AnalyticsNavElementName to Mixpanel section names (tracking plan). */
const NAV_SECTION_MAP: Record<string, string> = {
  discovery: "home",
  juisciGPT: "juiscigpt",
  for_you: "for you",
  search: "search",
  rooms: "rooms",
  resources: "resources",
  profile: "profile",
};

/**
 * The feed sections content can be navigated FROM. This is the closed set of
 * meaningful `navigation_context` values — page-type list names (article_page,
 * infographic_page, …) are NOT navigation contexts and map to `null`.
 */
type NavigationContext =
  | "discovery"
  | "for you"
  | "search"
  | "juiscigpt"
  | "playlist"
  | "conferences"
  | "resources";

/**
 * Map every `AnalyticsItemList` value to its `navigation_context`, or `null`
 * when the list name is a destination page-type rather than a feed section.
 * Exhaustive on purpose: a new `AnalyticsItemList` member fails the build here
 * until it is explicitly classified, so page-types can never silently leak
 * into `navigation_context` again.
 */
const NAVIGATION_CONTEXT_BY_LIST = {
  [AnalyticsItemList.HOME]: "discovery",
  [AnalyticsItemList.FORYOU]: "for you",
  [AnalyticsItemList.SEARCH]: "search",
  [AnalyticsItemList.PROFILE]: null,
  [AnalyticsItemList.DISCOVERY_MAIN_CAROUSEL]: "discovery",
  [AnalyticsItemList.DISCOVERY_SECOND_CAROUSEL]: "discovery",
  [AnalyticsItemList.DISCOVERY_PLAYLISTS]: "discovery",
  [AnalyticsItemList.ARTICLE_PAGE]: null,
  [AnalyticsItemList.AUTHOR_PAGE]: null,
  [AnalyticsItemList.JOURNAL_PAGE]: null,
  [AnalyticsItemList.PLAYLIST_PAGE]: "playlist",
  [AnalyticsItemList.PLAYLIST_PANEL]: "playlist",
  [AnalyticsItemList.GPT_ANSWER_SOURCES]: "juiscigpt",
  [AnalyticsItemList.ORGANISATION_PAGE]: null,
  [AnalyticsItemList.STORY_LIST_PAGE]: null,
  [AnalyticsItemList.VIDEO_ARTICLE_PAGE]: null,
  [AnalyticsItemList.VIDEO_STORY_FEED]: null,
  [AnalyticsItemList.INFOGRAPHIC_PAGE]: null,
  [AnalyticsItemList.ROOM_DISCOVERY]: "resources",
  [AnalyticsItemList.ROOM_CONGRESSES]: "conferences",
  [AnalyticsItemList.CONGRESS_SPACE]: "conferences",
  [AnalyticsItemList.ROOM_RESOURCES]: "resources",
  [AnalyticsItemList.ROOM_PODCAST]: "resources",
  // The practice rail sits on the home surface, next to the specialty carousels.
  [AnalyticsItemList.PRACTICE_RAIL]: "discovery",
  // The practice feed IS the discovery surface in its arm — same context as the
  // topic feed it replaces, so the two arms' funnels line up.
  [AnalyticsItemList.PRACTICE_FEED]: "discovery",
} as const satisfies Record<AnalyticsItemList, NavigationContext | null>;

// Indexed once at module load so lookups are O(1). `NAVIGATION_CONTEXT_BY_LIST`
// stays the exhaustive source of truth; this Map only mirrors it for runtime
// access by the plain `string` list names that reach the analytics layer.
const NAVIGATION_CONTEXT_LOOKUP: ReadonlyMap<string, NavigationContext | null> =
  new Map(Object.entries(NAVIGATION_CONTEXT_BY_LIST));

function mapListNameToNavigationContext(
  listName: string
): NavigationContext | null {
  const context = NAVIGATION_CONTEXT_LOOKUP.get(listName);
  return context === undefined ? null : context;
}

/**
 * A congress event already pushes the flat props the tracking plan asks for
 * (§V.2 identity + its own), and its Mixpanel name is its GA name — only
 * `origin`/`content_origin` still have to be derived at push time.
 */
function congressEvent(event: string): MixpanelMapping {
  return { resolve: (params) => ({ event, props: originProps(params) }) };
}

/**
 * Same shape as `congressEvent` for the visual-abstract family: those events
 * already push flat, snake_case props (`visual_abstract_id`, `family`,
 * `generator_version`, `latency_ms`, …), so the GA payload IS the Mixpanel
 * payload and only origin/content_origin are derived here.
 *
 * The GA name and the Mixpanel name deliberately differ on four of them —
 * `…generation_succeeded` → `visual_abstract_generated`, `…rendered` →
 * `…viewed`, `…report_submitted` → `…inaccuracy_reported`,
 * `…usefulness_answered` → `…rated`. The GA names predate the analytics board
 * and renaming them at the source would orphan the GA4/BigQuery history, which
 * is exactly what this mapping layer exists to avoid.
 */
function visualAbstractEvent(event: string): MixpanelMapping {
  return { resolve: (params) => ({ event, props: originProps(params) }) };
}

const MIXPANEL_EVENT_MAP: Record<string, MixpanelMapping> = {
  // ── Auth ──────────────────────────────────────────────────────────────
  login: { event: "logged_in" },
  logged_out: { event: "logged_out" },
  start_sign_up: { event: "sign_up_started" },
  names_submitted: { event: "names_submitted" },
  country_submitted: { event: "country_submitted" },
  hcp_registry_lookup_result: { event: "hcp_registry_lookup_result" },
  hcp_registry_profile_confirmed: { event: "hcp_registry_profile_confirmed" },
  hcp_registry_step_resolved_manually: {
    event: "hcp_registry_step_resolved_manually",
  },
  profession_submitted: { event: "profession_submitted" },
  main_specialty_submitted: { event: "main_specialty_submitted" },
  main_specialty_changed: { event: "main_specialty_changed" },
  matching_publications_submitted: { event: "matching_publications_submitted" },
  matching_publications_skipped: { event: "matching_publications_skipped" },
  relevant_topics_submitted: { event: "relevant_topics_submitted" },
  acquisition_channel_submitted: { event: "acquisition_channel_submitted" },
  feed_taste_explored: { event: "feed_taste_explored" },
  preference_onboarding_nudge_viewed: {
    event: "preference_onboarding_nudge_viewed",
  },
  preference_onboarding_opened: { event: "preference_onboarding_opened" },
  preference_onboarding_snoozed: { event: "preference_onboarding_snoozed" },
  preference_onboarding_completed: {
    event: "preference_onboarding_completed",
  },
  // The push opt-in pair. Both carry `surface`, and `push_opt_in_answered`
  // carries `outcome` — a static mapping forwards the GA payload as-is, so the
  // two land in Mixpanel with the properties the rate is sliced by. They MUST
  // be here: an event absent from this map is GA-only, and Mixpanel is the
  // acted single source of truth — which is how `step=notifications` came to
  // read 0 with nobody able to tell a removed step from a missing event.
  push_opt_in_shown: { event: "push_opt_in_shown" },
  push_opt_in_answered: { event: "push_opt_in_answered" },
  complete_onboarding: { event: "sign_up_completed" },

  // ── User / Profile ────────────────────────────────────────────────────
  view_user_profile: { event: null },
  view_own_profile: { event: "profile_opened" },
  update_user_interests: { event: "medical_specialty_added" },

  // ── Journal ───────────────────────────────────────────────────────────
  journal_follow: { event: "journal_followed" },
  journal_unfollow: { event: "journal_unfollowed" },

  // ── Content – View ────────────────────────────────────────────────────
  view_item: {
    resolve: (params) => {
      const item = firstEcommerceItem(params);
      const variant = item?.item_variant as string | undefined;
      const listName = item?.item_list_name as string | undefined;

      if (variant === "playlist") {
        return {
          event: "playlist_opened",
          props: { ...originProps(params) },
        };
      }
      return {
        event: "content_viewed",
        props: {
          ...(variant !== undefined ? { content_type: variant } : {}),
          navigation_context:
            listName !== undefined
              ? mapListNameToNavigationContext(listName)
              : undefined,
          location: params._contentLocation,
          ...originProps(params),
        },
      };
    },
  },

  // ── Content – Like ────────────────────────────────────────────────────
  article_like: {
    resolve: (params) => ({
      event: "content_liked",
      props: contentInteractionProps("article", params),
    }),
  },
  video_like: {
    resolve: (params) => ({
      event: "content_liked",
      props: contentInteractionProps("video", params),
    }),
  },

  infographic_like: {
    resolve: (params) => ({
      event: "content_liked",
      props: contentInteractionProps("infographic", params),
    }),
  },
  content_like: { event: null },

  // ── Content – Save ────────────────────────────────────────────────────
  article_save: {
    resolve: (params) => ({
      event: "content_saved",
      props: contentInteractionProps("article", params),
    }),
  },
  video_save: {
    resolve: (params) => ({
      event: "content_saved",
      props: contentInteractionProps("video", params),
    }),
  },

  infographic_save: {
    resolve: (params) => ({
      event: "content_saved",
      props: contentInteractionProps("infographic", params),
    }),
  },
  content_save: { event: null },
  playlist_save: {
    event: "playlist_saved",
    props: { save_status: "saved" },
  },
  playlist_unsave: {
    event: "playlist_saved",
    props: { save_status: "unsaved" },
  },

  // ── Content – Share ───────────────────────────────────────────────────
  article_share: {
    resolve: (params) => ({
      event: "content_shared",
      props: contentInteractionProps("article", params),
    }),
  },
  video_share: {
    resolve: (params) => ({
      event: "content_shared",
      props: contentInteractionProps("video", params),
    }),
  },

  infographic_share: {
    resolve: (params) => ({
      event: "content_shared",
      props: contentInteractionProps("infographic", params),
    }),
  },
  content_share: { event: null },
  // Not content, so not `content_shared`: a company page and a member's
  // profile ("Join me on Juisci", the app invite) are shares a recipient can
  // still sign up from, which the viral coefficient's numerator must count.
  organisation_share: { event: "organisation_shared" },
  profile_share: { event: "profile_shared" },
  playlist_share: {
    resolve: (params) => ({
      event: "content_shared",
      props: contentInteractionProps("playlist", params),
    }),
  },

  // ── Content – Consumed / Played ───────────────────────────────────────
  article_consumed: {
    resolve: (params) => ({
      event: "content_consumed",
      exclusive: true,
      props: {
        content_type: "article",
        ...originProps(params),
      },
    }),
  },
  // `content_quartile_consumed` belongs to the progress milestones below: a
  // start has no share of the video in it.
  video_played: {
    resolve: (params) => {
      const item = firstEcommerceItem(params);
      return {
        event: "content_played",
        exclusive: true,
        props: {
          content_type: "video",
          ...originProps(params),
          ...(typeof item?.language === "string"
            ? { language: item.language }
            : {}),
        },
      };
    },
  },

  content_progress_milestone: {
    resolve: (params) => ({
      event: "content_quartile_consumed",
      exclusive: true,
      props: {
        content_type: params.content_type,
        content_id: params.content_id,
        progress_percent: params.progress_percent,
        elapsed_seconds: params.elapsed_seconds,
        surface: params.surface,
        ...originProps(params),
      },
    }),
  },

  // ── Audio ─────────────────────────────────────────────────────────────
  audio_played: {
    resolve: (params) => ({
      event: "article_listened",
      exclusive: true,
      props: {
        ...originProps(params),
        percentage: 0,
      },
    }),
  },
  audio_progress: {
    resolve: (params) => {
      const depth = params.listening_depth;
      if (typeof depth === "number" && (depth === 5 || depth === 10))
        return null;
      return {
        event: "article_listened",
        exclusive: true,
        props: {
          ...originProps(params),
          ...(typeof depth === "number" ? { percentage: depth } : {}),
        },
      };
    },
  },
  audio_complete: {
    resolve: (params) => ({
      event: "article_listened",
      exclusive: true,
      props: {
        ...originProps(params),
        percentage: 100,
      },
    }),
  },

  // ── Article Tabs ──────────────────────────────────────────────────────
  article_tabs_clicked: { event: "article_tabs_clicked" },

  // ── GPT ───────────────────────────────────────────────────────────────
  juiscigpt_session_started: { event: "juiscigpt_session_started" },
  juiscigpt_conversation_shared: { event: "juiscigpt_conversation_shared" },
  juiscigpt_conversation_share_revoked: {
    event: "juiscigpt_conversation_share_revoked",
  },
  juiscigpt_shared_conversation_opened: {
    event: "juiscigpt_shared_conversation_opened",
  },
  // The step between the open and the question: `visitor_auth` says whether
  // the reader had to sign in on the page to resume.
  juiscigpt_shared_conversation_resume_clicked: {
    event: "juiscigpt_shared_conversation_resume_clicked",
  },
  // Logged-out JuisciGPT (PRD "Juisci GPT sans compte"). Registered so the
  // anonymous question → signup funnel reaches Mixpanel, where it is read.
  gpt_anonymous_question_asked: { event: "gpt_anonymous_question_asked" },
  gpt_anonymous_limit_reached: { event: "gpt_anonymous_limit_reached" },
  gpt_anonymous_signup_clicked: { event: "gpt_anonymous_signup_clicked" },
  gpt_patient_data_redacted: { event: "gpt_patient_data_redacted" },
  // The wait for the first words and for the answer (docs/juisci-gpt-pre-answer.md).
  juiscigpt_answer_wait_ended: { event: "juiscigpt_answer_wait_ended" },
  gpt_copy_answer: { event: "juiscigpt_answer_interacted_with" },
  article_chat_question: { event: "question_asked_to_a_paper" },
  // Room suggestion (docs/juisci-gpt-room-suggestion.md). Registered here on
  // purpose: an event absent from this map is GA-only and never reaches
  // Mixpanel, so the card→room conversion funnel would be invisible in the tool
  // that is the source of truth for our metrics. The join numerator itself is
  // the existing `room_joined` with room_discovery_channel = "juiscigpt".
  juiscigpt_room_suggestion_shown: {
    event: "juiscigpt_room_suggestion_shown",
  },
  juiscigpt_room_suggestion_clicked: {
    event: "juiscigpt_room_suggestion_clicked",
  },
  juiscigpt_room_suggestion_dismissed: {
    event: "juiscigpt_room_suggestion_dismissed",
  },

  // ── Visual Abstract ───────────────────────────────────────────────────
  // The whole feature was GA4-only until here: an event absent from this map
  // never reaches Mixpanel, which is where the analytics board reads the
  // generation funnel, the reliability guardrails and the share rate.
  //
  // The PUBLIC surfaces (`visual_abstract_public_open` / `…public_share` /
  // `…article_click` / `…signup_click`) are deliberately NOT listed. `/v/:token` renders outside
  // the shell that initialises Mixpanel, on purpose — a capability URL must
  // never reach an autocapturing SDK (`.knowledge/mixpanel-capability-url-leak
  // .md`) — so a mapping for them would be dead code that reads as coverage.
  // The recipient side is measured from the article click-through instead,
  // whose `utm_source=visual_abstract` Mixpanel records as first-touch.
  visual_abstract_cta_shown: visualAbstractEvent("visual_abstract_cta_shown"),
  visual_abstract_requested: visualAbstractEvent("visual_abstract_requested"),
  // The refusals. Read them BESIDE `visual_abstract_requested`, never inside the
  // generation funnel: nothing was requested here, the feature declined before
  // offering anything, so folding these in would deflate the success rate with
  // papers that never entered the pipeline.
  visual_abstract_unavailable: visualAbstractEvent(
    "visual_abstract_unavailable"
  ),
  visual_abstract_generation_succeeded: visualAbstractEvent(
    "visual_abstract_generated"
  ),
  visual_abstract_generation_failed: visualAbstractEvent(
    "visual_abstract_generation_failed"
  ),
  visual_abstract_rendered: visualAbstractEvent("visual_abstract_viewed"),
  visual_abstract_opened_from_library: visualAbstractEvent(
    "visual_abstract_opened_from_library"
  ),
  // One Mixpanel event for BOTH share affordances, split by `channel`: the
  // board's share rate is shares over views, and a link share and a PNG export
  // that landed in two different events would have to be summed by hand in
  // every chart that asks whether a poster was shared at all.
  visual_abstract_shared: visualAbstractEvent("visual_abstract_shared"),
  visual_abstract_image_share: visualAbstractEvent("visual_abstract_shared"),
  visual_abstract_share_revoked: visualAbstractEvent(
    "visual_abstract_share_revoked"
  ),
  visual_abstract_report_submitted: visualAbstractEvent(
    "visual_abstract_inaccuracy_reported"
  ),
  // Reaches Mixpanel from the PREVIEW only. The same GA event also fires on the
  // public poster, where nothing is initialised — so read `visual_abstract_rated`
  // as the owner's verdict, never as "what readers think of the poster", and
  // segment on `surface` if the public side is ever wired server-side.
  visual_abstract_usefulness_answered: visualAbstractEvent(
    "visual_abstract_rated"
  ),

  // ── Search (conditional: GPT vs content search) ───────────────────────
  search: {
    resolve: (params) => {
      const search = params.search as
        | Record<string, string | number | boolean | null | undefined>
        | undefined;
      const type = search?.type;
      if (type === "gpt_suggested" || type === "gpt_manual") {
        // `type` came from `search.type`, so `search` is defined here — narrow it
        // once and read fields directly (no optional chaining, per AGENTS.md).
        if (search === undefined)
          throw new Error(
            "should not happen: search is set when type is a gpt type"
          );
        const conversationHistory = search.conversation_history;
        return {
          event: "question_asked_to_juiscigpt",
          props: {
            type,
            // The precise input method — separates follow-up chips and landing
            // pills, which `type` collapses into "gpt_suggested".
            question_origin:
              typeof search.origin === "string" ? search.origin : null,
            conversation_history:
              conversationHistory === "new" || conversationHistory === "old"
                ? conversationHistory
                : null,
            question_number:
              typeof search.number === "number" ? search.number : null,
            // Mode Router (docs/gpt-mode-router.md).
            selected_mode:
              typeof search.selected_mode === "string"
                ? search.selected_mode
                : null,
            resolved_mode:
              typeof search.resolved_mode === "string"
                ? search.resolved_mode
                : null,
            was_forced:
              typeof search.was_forced === "boolean" ? search.was_forced : null,
            classifier_confidence:
              typeof search.classifier_confidence === "number"
                ? search.classifier_confidence
                : null,
          },
        };
      }
      return {
        event: "content_searched",
        exclusive: true,
        props: {
          type: typeof type === "string" ? type : null,
          search: typeof search?.term === "string" ? search.term : null,
          results_count:
            typeof search?.results_count === "number"
              ? search.results_count
              : null,
          source: typeof search?.source === "string" ? search.source : null,
          scope: typeof search?.scope === "string" ? search.scope : null,
          ...originProps(params),
        },
      };
    },
  },
  search_tab_selected: {
    resolve: (params) => {
      const tab = params.search_tab as
        Record<string, string | undefined> | undefined;
      return {
        event: "search_tab_selected",
        exclusive: true,
        props: {
          from: typeof tab?.from === "string" ? tab.from : null,
          to: typeof tab?.to === "string" ? tab.to : null,
          search: typeof tab?.term === "string" ? tab.term : null,
          ...originProps(params),
        },
      };
    },
  },
  search_result_clicked: {
    resolve: (params) => {
      const result = params.search_result as
        Record<string, string | number | undefined> | undefined;
      return {
        event: "search_result_clicked",
        exclusive: true,
        props: {
          search: typeof result?.term === "string" ? result.term : null,
          category:
            typeof result?.category === "string" ? result.category : null,
          result_type:
            typeof result?.result_type === "string" ? result.result_type : null,
          result_id:
            typeof result?.result_id === "string" ? result.result_id : null,
          position:
            typeof result?.position === "number" ? result.position : null,
          scope: typeof result?.scope === "string" ? result.scope : null,
          ...originProps(params),
        },
      };
    },
  },
  mesh_suggestion_clicked: {
    resolve: (params) => {
      const mesh = params.mesh_suggestion as
        Record<string, string | boolean | undefined> | undefined;
      return {
        event: "mesh_suggestion_clicked",
        exclusive: true,
        props: {
          search: typeof mesh?.term === "string" ? mesh.term : null,
          is_primary_specialty:
            typeof mesh?.is_primary_specialty === "boolean"
              ? mesh.is_primary_specialty
              : null,
          ...originProps(params),
        },
      };
    },
  },

  // ── Playlist – Create ─────────────────────────────────────────────────
  playlist_created: {
    resolve: (params) => ({
      event: "playlist_created",
      props: {
        navigation_context:
          typeof params.origin === "string"
            ? mapListNameToNavigationContext(params.origin)
            : undefined,
        ...originProps(params),
      },
    }),
  },

  // ── Onboarding (new signup flow) ──────────────────────────────────────
  signup_sheet_opened: { event: "signup_sheet_opened" },
  signup_sheet_dismissed: { event: "signup_sheet_dismissed" },
  auth_method_selected: { event: "auth_method_selected" },
  auth_failed: { event: "auth_failed" },
  // The email-OTP funnel. All three MUST be here: an event absent from this map
  // reaches GA4 and never reaches Mixpanel, which is the tool this feature's
  // target and guardrail are read in. The static mapping forwards the GA
  // payload as-is, so `intent`, `reason`, `attempts` and `latency_ms` land with
  // the event and the funnel is sliceable by all of them.
  otp_requested: { event: "otp_requested" },
  otp_verified: { event: "otp_verified" },
  otp_failed: { event: "otp_failed" },
  signup_step_viewed: { event: "signup_step_viewed" },
  signup_step_back: { event: "signup_step_back" },
  // The healthcare-professional gate. Carries `outcome`, so the static mapping
  // forwards the GA payload as-is and the declared-non-HCP share is sliceable.
  hcp_gate_answered: { event: "hcp_gate_answered" },
  // The practice question reached somebody, with what the agent found for them.
  // The DENOMINATOR of everything the practice screens are judged on: the
  // confirmed event below fires after the last signup slide and counts nobody who
  // left in between. Carries `flow`, `screen`, `recognised_practice_count` and
  // `catalogue_size` as-is, which is what makes the agent's yield readable.
  practice_question_shown: { event: "practice_question_shown" },
  // "Edit my practices" on the recap. The intent to correct the agent, whether
  // or not signup survives the two slides after it.
  practice_recap_edit_clicked: { event: "practice_recap_edit_clicked" },
  // The Practice Profiles slide's answer. Carries `changed_selection`, the
  // catalogue/confirmed counts and the gross added/removed deltas, so the static
  // mapping forwards them as-is: the confirmation rate this screen exists for is
  // read in Mixpanel.
  practice_profiles_confirmed: { event: "practice_profiles_confirmed" },
  // "Later" on the signup practice slide. Carries `skipped_from` and the
  // agent's yield, forwarded as-is.
  practice_question_skipped: { event: "practice_question_skipped" },
  // The offer that ran out of time. Carries `abandoned_at`, which is the whole
  // point of the event: from outside, a warm timeout and a cold one both look
  // like "this Specialty has no catalogue".
  practice_offer_abandoned: { event: "practice_offer_abandoned" },
  // The catalogue had nothing for what a practitioner typed. Carries `query`,
  // `flow` and `specialty_uid`, forwarded as-is: a missing practice is only
  // actionable per Specialty, and the query is the thing the medical team reads.
  practice_search_no_result: { event: "practice_search_no_result" },
  // The Explore gate held an already-signed-up practitioner in front of the
  // practice question. The denominator of the confirmation rate above: without
  // it, abandoning the gate and never meeting it are the same number.
  practice_profiling_gate_shown: { event: "practice_profiling_gate_shown" },
  // The same gate, closed rather than answered — the numerator the shown event
  // has never had.
  practice_profiling_gate_dismissed: {
    event: "practice_profiling_gate_dismissed",
  },
  pub_search_no_results: { event: "pub_search_no_results" },
  pub_search_error: { event: "pub_search_error" },

  // ── Practice rail ─────────────────────────────────────────────────────
  // The rail's own funnel: shown, then opened. Both carry `corpus` and, off the
  // aggregate, `practice_profile_uid`, so the rate is readable per practice.
  //
  // ⚠️ The five SERVER events (`practice_rail_publications`,
  // `practice_rail_catalogue`, `practice_rail_mesh_terms`,
  // `practice_rail_all_practices`, `practice_rail_all_practices_mesh_terms`)
  // answer a DIFFERENT question and must not be read as engagement. They fire
  // when the API builds a response and carry what the corpus gave it — `total`,
  // `terms`, `held`/`served`/`capped`/`unsupported` — which is how a thin or
  // empty rail is caught. Nothing in them says a reader saw or touched anything.
  practice_profile_rail_viewed: { event: "practice_profile_rail_viewed" },
  practice_profile_rail_article_clicked: {
    event: "practice_profile_rail_article_clicked",
  },

  // ── Errors ──────────────────────────────────────────────────────────────
  error_encountered: { event: "error_encountered" },

  // ── Notifications ─────────────────────────────────────────────────────
  notification_clicked: { event: "notification_tapped" },

  // ── Pop-up announcements ──────────────────────────────────────────────
  // The full funnel of the in-app announcement sheet: shown → acted on, or
  // shown → closed. All three are registered because the CTA rate is read in
  // Mixpanel and `announcement_viewed` is its DENOMINATOR — leaving the
  // impression GA-only would make every announcement look like it converted at
  // an unknown rate. The GA payloads are already the properties the funnel is
  // sliced by (`announcement_slug`, `room_id`, `action`, `method`), so a static
  // mapping forwards them as they are.
  announcement_viewed: { event: "announcement_viewed" },
  announcement_cta_clicked: { event: "announcement_cta_clicked" },
  announcement_dismissed: { event: "announcement_dismissed" },

  // ── Shared links ──────────────────────────────────────────────────────
  // The top of the share → sign-up funnel (`docs/viral-coefficient.md`): a
  // visit arrived from a shared link. Fired at boot, so its mirror waits for
  // Mixpanel to start (`HELD_UNTIL_MIXPANEL_STARTS`). The GA payload is what
  // the funnel is sliced by (`share_kind`, `content_type`, `logged_in`).
  shared_link_opened: { event: "shared_link_opened" },

  // ── Install ask ───────────────────────────────────────────────────────
  // The acquisition funnel of every shared link: shown → tapped through to the
  // store, or shown → waved away. All three are registered for the reason the
  // announcement trio is — `install_wall_viewed` is the DENOMINATOR, and an
  // impression left GA-only makes the tap rate unreadable.
  //
  // The GA payload is already what the funnel is sliced by: `install_wall_kind`
  // (the wall and the sheet are different offers and must never be summed),
  // `install_origin` plus the one id column it fills (`article_slug`,
  // `playlist_id`, `room_id`, `invite_code`), and the method on a dismissal. A
  // static mapping forwards them unchanged.
  install_wall_viewed: { event: "install_wall_viewed" },
  install_wall_app_clicked: { event: "install_wall_app_clicked" },
  install_wall_dismissed: { event: "install_wall_dismissed" },

  // ── Native app lifecycle ──────────────────────────────────────────────
  // The installed app started or came back to the screen (the iOS and Android
  // shells report it over the bridge). The GA payload is already the Mixpanel
  // one: `foreground_source` (`cold_start` | `resume`) and `deep_link`, the
  // host and route family of the link that opened the app, or null.
  app_foregrounded: { event: "app_foregrounded" },

  // ── Rooms ─────────────────────────────────────────────────────────────
  room_join: { event: "room_joined" },
  room_opened: { event: "room_opened" },
  // The feed room-card impression — the DENOMINATOR the PRD's primary metric
  // compares the JuisciGPT card against. Registered here on purpose: an event
  // absent from this map is GA4-only and never reaches Mixpanel, which is where
  // the comparison is actually read.
  room_recommendation_shown: { event: "room_recommendation_shown" },
  // The partner objection and the room welcome sheet (PRD section 5).
  partner_objection_shown: { event: "partner_objection_shown" },
  partner_objection_answered: { event: "partner_objection_answered" },
  room_welcome_shown: { event: "room_welcome_shown" },
  room_welcome_completed: { event: "room_welcome_completed" },
  room_info_opened: { event: "room_info_opened" },
  room_qr_code_opened: {
    resolve: (params) => ({
      event: "room_qr_code_opened",
      exclusive: true,
      props: { room_name: params.room_name as string },
    }),
  },

  // ── Congress Space ────────────────────────────────────────────────────
  // Content attached to a congress (publications, videos, collections, …) is
  // NOT listed here: it keeps firing the generic content events, tagged with
  // the `congress_space` list so `navigation_context` reads "conferences".
  congress_countdown_shown: congressEvent("congress_countdown_shown"),
  congress_countdown_tapped: congressEvent("congress_countdown_tapped"),
  congress_space_viewed: congressEvent("congress_space_viewed"),
  congress_sessions_card_viewed: congressEvent("congress_sessions_card_viewed"),
  congress_sessions_opened: congressEvent("congress_sessions_opened"),
  congress_session_link_clicked: congressEvent("congress_session_link_clicked"),
  congress_pdf_card_opened: congressEvent("congress_pdf_card_opened"),

  // ── Filters ───────────────────────────────────────────────────────────
  set_filter: {
    resolve: (params) => ({
      event: "filter_applied",
      exclusive: true,
      props: {
        applied_filter: params.applied_filter,
        room_name: params.room_name ?? null,
      },
    }),
  },
  filter: { event: "filter_applied" },

  // ── Navigation (conditional: bottom bar, language, share room) ────────
  nav_click: {
    resolve: (params) => {
      switch (params.nav_element) {
        case "bottom_bar": {
          const raw = params.nav_element_name as string;
          const loc = params._contentLocation;
          return {
            event: "section_navigated_to",
            exclusive: true,
            props: {
              section: NAV_SECTION_MAP[raw] ?? raw,
              origin: loc === "room" ? "room" : "public app",
            },
          };
        }
        case "change_language": {
          return { event: "language_switched" };
        }
        case "share_room": {
          return {
            event: "room_shared",
            exclusive: true,
            props: {
              room_name: params.nav_element_name as string,
              format: params.format as string,
            },
          };
        }
        default: {
          return null;
        }
      }
    },
  },
};

/**
 * Flatten GA4 `ecommerce.items[0]` into top-level Mixpanel properties
 * and strip the ecommerce wrapper which is GA4-specific.
 *
 * NOTE: `item_brand` is intentionally NOT mapped to `origin`.
 * Each Mixpanel event mapping explicitly sets `origin` to the
 * correct location value ("public app" / "room") via `getContentLocation()`.
 */
function flattenEcommerceForMixpanel(
  props: Record<string, unknown>
): Record<string, unknown> {
  const { ecommerce, ...rest } = props;
  if (!ecommerce || typeof ecommerce !== "object") return props;

  const items = (ecommerce as Record<string, unknown>).items;
  if (!Array.isArray(items) || items.length === 0) return rest;

  const item = items[0] as Record<string, unknown>;
  return {
    ...rest,
    content_id: item.item_id,
    content_name: item.item_name,
    content_category: item.item_category,
    list_name: item.item_list_name,
    ...(item.language != null && { language: item.language }),
    ...(item.audio_duration_ms != null && {
      audio_duration_ms: item.audio_duration_ms,
    }),
    ...(item.quantity != null && { quantity: item.quantity }),
  };
}

const debug = new DebugLogger("Analytics");
// Ensure dataLayer is initialized (global.d.ts declares it on Window)
window.dataLayer = window.dataLayer ?? [];
// debug.mute();

export async function gaInitializeTagManager() {
  if (env.gtmId === null) throw new Error("gtmId is not configured");
  // Tag Manager's own snippet, without an inline script: the
  // Content-Security-Policy of the webapp allows no inline script that the
  // served page does not hold (front-serve/index.ts).
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(env.gtmId)}`;
  document.head.append(script);
  debug.log("GTM initialized");
}

/** Set user properties on Mixpanel (people.set). No-op in dev or before init. */
export function mixpanelSetUserProperties(properties: Record<string, unknown>) {
  if (env.appEnv === "development") return;
  if (!mixpanelInitialized) return;
  mixpanel.people.set(properties);
}

/**
 * Events pushed before the app shell starts Mixpanel (`useMixpanel`), whose
 * mirror waits for it instead of being dropped. `shared_link_opened` fires at
 * boot, before the first render. Any other event pushed before the start keeps
 * its current GA-only behavior. On `/v/:token` the shell never mounts, so a
 * held mirror is never sent (`.knowledge/mixpanel-capability-url-leak.md`).
 */
const HELD_UNTIL_MIXPANEL_STARTS: ReadonlySet<string> = new Set([
  "shared_link_opened",
]);

/** How often a visible page sends its engagement time to GA4. */
const ENGAGEMENT_HEARTBEAT_MS = 30_000;

/**
 * GA4 sends the engagement time of a page with the next event, or on page
 * hide. When the native shell is killed, the phone locks before the beacon
 * leaves, or the network drops, the time of the last page is lost, and the
 * client dashboard under-counts the time in a space
 * (`analytics_daily_users.total_engagement_time_msec`). An
 * `engagement_heartbeat` every 30 s of visible, focused page carries the time
 * accrued since the last event, so at most 30 s is lost. A hidden or unfocused
 * page accrues no engagement time and sends nothing.
 */
export function gaStartEngagementHeartbeat() {
  setInterval(() => {
    if (document.visibilityState !== "visible" || !document.hasFocus()) return;
    gaPushEvent("engagement_heartbeat");
  }, ENGAGEMENT_HEARTBEAT_MS);
}

export function gaPushEvent(event: string, params?: DataLayerEntry) {
  // dataLayer is guaranteed to be initialized at module load
  const dataLayer = window.dataLayer;

  if (params !== undefined && Object.keys(params).includes("ecommerce")) {
    // NOTE: Safety for unexpected edge cases.
    dataLayer.push({ ecommerce: null });
  }

  const isProduction = window.location.hostname.includes("app.juisci");
  const environmentName = isProduction ? "prod" : "dev";
  const environment = {
    ...(typeof params?.environment === "object" && params.environment !== null
      ? params.environment
      : {}),
    name: environmentName,
  };

  const eventId = newEventId();

  // Inject user.account_id on every event so GTM maps it to GA4 user_id, not just page_view.
  const currentUser = getCurrentUserData();
  const paramsUser =
    typeof params?.user === "object" && params.user !== null ? params.user : {};

  const payload: DataLayerEntry = {
    event,
    ...params,
    environment,
    ...(currentUser === null
      ? {}
      : { user: { ...paramsUser, account_id: currentUser.uid } }),
    event_id: eventId,
  };
  debug.log(payload);

  dataLayer.push(payload);

  // Mirror to Mixpanel with unified event names
  // Capture location/origin eagerly so the URL is read at call time, not later.
  const contentLocation = getContentLocation();
  const contentOrigin = getContentOrigin();

  if (env.appEnv === "development") return;
  const mirror = () => {
    if (!(event in MIXPANEL_EVENT_MAP)) return;
    const entry = MIXPANEL_EVENT_MAP[event];

    let mpEvent: string;
    let extraProps: Record<string, unknown> = {};

    let exclusive = false;

    if ("resolve" in entry) {
      if (contentOrigin === null) {
        // Known race: an event fired on a room page before the room query
        // resolved, so the Mixpanel mirror for it is dropped (GTM/GA above already
        // got the event). Only a DYNAMIC mapping reads the origin; a static one
        // forwards the GA payload and has nothing to lose to the race, so it is
        // mirrored regardless. A pop-up shown on a room page at boot was otherwise
        // missing from its own denominator. Measured at WARNING with a stable fingerprint — as a
        // console.error it paged as a Sentry error on every occurrence
        // (JUISCI-APP-24A) for a condition the code handles by design.
        Sentry.captureMessage(
          "analytics: room data not loaded, Mixpanel mirror dropped",
          {
            level: "warning",
            tags: { source: "trackEvent", event },
            fingerprint: ["analytics-room-data-not-loaded"],
          }
        );
        return;
      }
      const result = entry.resolve({
        ...params,
        _contentLocation: contentLocation,
        _contentOrigin: contentOrigin,
      });
      if (!result) return;
      mpEvent = result.event;
      extraProps = result.props ?? {};
      exclusive = result.exclusive ?? false;
    } else {
      if (entry.event === null) return;
      mpEvent = entry.event;
      extraProps = entry.props ?? {};
    }

    if (exclusive) {
      mixpanel.track(mpEvent, { ...extraProps, $insert_id: eventId });
    } else {
      const { event: _, ...rest } = payload;
      const flat = flattenEcommerceForMixpanel(rest);
      mixpanel.track(mpEvent, { ...flat, ...extraProps, $insert_id: eventId });
    }
  };
  if (mixpanelInitialized) {
    mirror();
    return;
  }
  if (HELD_UNTIL_MIXPANEL_STARTS.has(event)) whenMixpanelInitialized(mirror);
}

/**
 * Track how long a user viewed a piece of content. Sends a `content_viewed`
 * event to Mixpanel with a `duration` property (seconds). Called on page
 * unmount / visibility change so the entry `content_viewed` (no duration)
 * counts views, while this one measures engagement.
 *
 * Filter in Mixpanel:
 *   meaningful views  → `content_viewed WHERE duration >= 30`
 *   view count        → `content_viewed WHERE duration IS NOT SET`
 */
export function trackContentViewDuration(params: {
  contentId: string;
  contentName: string;
  contentType: string;
  listName: string;
  durationSeconds: number;
  location: "public app" | "room" | "profile";
  contentOrigin: string;
}) {
  if (env.appEnv === "development") return;
  if (!mixpanelInitialized) return;
  if (params.durationSeconds < 1) return;

  const eventId = newEventId();

  mixpanel.track("content_viewed", {
    content_id: params.contentId,
    content_name: params.contentName,
    content_type: params.contentType,
    navigation_context: mapListNameToNavigationContext(params.listName),
    location: params.location,
    origin: params.location,
    content_origin: params.contentOrigin,
    duration: params.durationSeconds,
    $insert_id: eventId,
  });
}

/**
 * One stretch of foreground time in a visit that started from one of our
 * emails (`useEmailVisitEngagement`). Sent when the tab is hidden or closed,
 * so it rides `sendBeacon`: a plain request is cancelled with the page.
 *
 * A visit is several of these (the reader switches tabs and comes back), all
 * carrying the same `visit_id`. Read in Mixpanel:
 *   time per visit  → sum of `duration_seconds` by `visit_id`
 *   time per reader → sum of `duration_seconds` per user, then the average
 */
export function trackEmailVisitEngagement(params: {
  visitId: string;
  utmSource: string;
  utmContent: string | null;
  durationSeconds: number;
}) {
  if (env.appEnv === "development") return;
  if (!mixpanelInitialized) return;
  if (params.durationSeconds < 1) return;

  mixpanel.track(
    "email_visit_engaged",
    {
      visit_id: params.visitId,
      utm_source: params.utmSource,
      utm_content: params.utmContent,
      duration_seconds: params.durationSeconds,
      $insert_id: newEventId(),
    },
    { transport: "sendBeacon" }
  );
}

// ── Content consumption ─────────────────────────────────────────────────
// One scale for every format: the share of a content item consumed in one
// open. `useContentProgress` owns the open; these only send. The definition
// of 100% per type lives in docs/content-consumption-tracking.md.

export type ConsumedContentType =
  | "article"
  | "article_audio"
  | "podcast_episode"
  | "video"
  | "infographic"
  | "playlist";

export type ContentExitAction =
  | "back"
  | "next_content"
  | "chat"
  | "source_link"
  | "share"
  | "app_background"
  | "unknown";

export const CONTENT_PROGRESS_MILESTONES = [25, 50, 75, 90, 100] as const;
export type ContentProgressMilestone =
  (typeof CONTENT_PROGRESS_MILESTONES)[number];

export type ContentOpenContext = {
  contentType: ConsumedContentType;
  contentId: string;
  surface: "mobile" | "desktop";
  location: "public app" | "room" | "profile";
  contentOrigin: string;
};

/** Live, once per milestone and open. Mirrored to Mixpanel by the map above. */
export function trackContentProgressMilestone(
  open: ContentOpenContext,
  milestone: {
    progressPercent: ContentProgressMilestone;
    elapsedSeconds: number;
  }
) {
  gaPushEvent("content_progress_milestone", {
    content_type: open.contentType,
    content_id: open.contentId,
    progress_percent: milestone.progressPercent,
    elapsed_seconds: milestone.elapsedSeconds,
    surface: open.surface,
    location: open.location,
    content_origin: open.contentOrigin,
  });
}

/**
 * Sent at the end of an open, often as the page goes away: the Mixpanel copy
 * rides `sendBeacon` (see `trackEmailVisitEngagement`), so these events stay
 * out of `MIXPANEL_EVENT_MAP` and are mirrored here.
 */
function pushExitEvent(event: string, params: Record<string, unknown>) {
  gaPushEvent(event, params);
  if (env.appEnv === "development") return;
  if (!mixpanelInitialized) return;
  mixpanel.track(
    event,
    { ...params, $insert_id: newEventId() },
    { transport: "sendBeacon" }
  );
}

export type ArticleReadDetails = {
  maxScrollPx: number;
  pageHeightPx: number;
  sectionsReached: number;
  sectionsTotal: number;
  tabsVisited: string;
};

/** The figures of one open that only some types have; null elsewhere. */
export type ContentReadDetails = {
  mediaDurationSeconds: number | null;
  playlistItems: { total: number; seen: number; opened: number } | null;
  article: ArticleReadDetails | null;
};

export function trackContentReadSummary(
  open: ContentOpenContext,
  summary: {
    progressPercent: number;
    durationSeconds: number;
    visibleSeconds: number;
    exitAction: ContentExitAction;
    details: ContentReadDetails;
  }
) {
  const { details } = summary;
  pushExitEvent("content_read_summary", {
    content_type: open.contentType,
    content_id: open.contentId,
    surface: open.surface,
    location: open.location,
    content_origin: open.contentOrigin,
    progress_percent: summary.progressPercent,
    completed: summary.progressPercent >= 90,
    duration_seconds: summary.durationSeconds,
    visible_seconds: summary.visibleSeconds,
    exit_action: summary.exitAction,
    media_duration_seconds: details.mediaDurationSeconds,
    items_total:
      details.playlistItems === null ? null : details.playlistItems.total,
    items_seen:
      details.playlistItems === null ? null : details.playlistItems.seen,
    items_opened:
      details.playlistItems === null ? null : details.playlistItems.opened,
    max_scroll_px:
      details.article === null ? null : details.article.maxScrollPx,
    page_height_px:
      details.article === null ? null : details.article.pageHeightPx,
    sections_reached:
      details.article === null ? null : details.article.sectionsReached,
    sections_total:
      details.article === null ? null : details.article.sectionsTotal,
    tabs_visited: details.article === null ? null : details.article.tabsVisited,
  });
}

/** One per article section seen at least 1 s, sent before the summary. */
export function trackArticleSectionRead(
  open: ContentOpenContext,
  section: {
    section: string;
    sectionOrder: number;
    seconds: number;
    reached: boolean;
  }
) {
  pushExitEvent("article_section_read", {
    content_id: open.contentId,
    section: section.section,
    section_order: section.sectionOrder,
    seconds: section.seconds,
    reached: section.reached,
    surface: open.surface,
  });
}
